/* eslint-disable import/no-unresolved */
import {
  KlevuConfig,
  KlevuFetch,
  KlevuDomEvents,
  KlevuEvents,
  search,
  sendSearchEvent,
  listFilters,
  applyFilterWithManager,
  FilterManager,
} from 'https://cdn.skypack.dev/-/@klevu/core@v5.2.3-HNRf1HrJ9AqvnoHcX5mg/dist=es2019,mode=imports,min/optimized/@klevu/core.js'

KlevuConfig.init({
  apiKey: `${window.theme.klevu.apiKey}`,
  url: `https://${window.theme.klevu.url}/cs/v2/search`,
})

class KlevuSearch extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.searchTerm = this.getQueryParam('q')
    this.manager = new FilterManager()
    this.sorting = 'RELEVANCE'
    this.page = 1
    this.totalShowing = 0

    this.loadResults(this.sorting)
  }

  setupListeners () {
    super.setupListeners()

    this.handleOptionTrigger = this.handleOptionTrigger.bind(this)
    this.listeners.add(document, 'click', this.handleOptionTrigger)

    this.handleClearTrigger = this.handleClearTrigger.bind(this)
    this.listeners.add(document, 'click', this.handleClearTrigger)

    this.handleFacetRemove = this.handleFacetRemove.bind(this)
    this.listeners.add(document, 'click', this.handleFacetRemove)

    document.addEventListener(
      KlevuDomEvents.FilterSelectionUpdate,
      this.handleFilterUpdate.bind(this),
    )
  }

  handleOptionTrigger (event) {
    const optionTrigger = event.target.closest(
      '[data-klevu-search-el="option"]',
    )

    if (!optionTrigger) return

    this.setAttribute('aria-busy', true)
    this.manager.toggleOption(optionTrigger.name, optionTrigger.value)

    this.unsetBusyState(600)
  }

  handleClearTrigger (event) {
    const facetRemoveButton = event.target.closest(
      '[data-collection-filters-el="removeAll"]',
    )

    if (!facetRemoveButton) return
    event.preventDefault()

    this.setAttribute('aria-busy', true)

    this.manager.clear()
    this.loadResults(this.sorting)

    this.unsetBusyState(600)
  }

  handleFacetRemove (event) {
    const facetRemoveButton = event.target.closest(
      '[data-collection-filters-el="facet-remove"]',
    )

    if (!facetRemoveButton) return

    this.manager.deselectOption(
      facetRemoveButton.name,
      facetRemoveButton.value,
    )
    this.loadResults(this.sorting)
  }

  async handleFilterUpdate () {
    this.setAttribute('aria-busy', true)

    this.page = 1
    this.removeExistingLoadMoreButton()
    this.loadResults(this.sorting)

    // Update aria-hidden attribute
    const hasActiveFilters = this.manager.selectedFilters().length > 0
    this.els['aside-active-indicator'].element.setAttribute(
      'aria-hidden',
      !hasActiveFilters,
    )

    this.unsetBusyState(600)
  }

  handleSortingUpdate (event) {
    this.setAttribute('aria-busy', true)
    const selectedValue = event.target.dataset.value

    this.sorting = selectedValue
    this.loadResults(this.sorting)

    this.unsetBusyState(600)
  }

  async loadResults (sorting) {
    this.removeExistingLoadMoreButton()

    // Fetch search results
    const result = await KlevuFetch(
      search(
        this.searchTerm,
        {
          id: 'search',
          fields: ['url'],
          limit: 48,
          sort: sorting,
        },
        listFilters({
          filterManager: this.manager,
        }),
        applyFilterWithManager(this.manager),
        sendSearchEvent(),
      ),
    )
    const resultSearch = result.queriesById('search')
    this.resultSearch = resultSearch

    const records = resultSearch?.records
    const recordsTotal = resultSearch.meta.totalResultsFound

    this.records = records
    this.recordsTotal = recordsTotal
    this.totalShowing = records.length
    this.updateResultsLabel()

    if (recordsTotal < 1) {
      // Show empty panel if no results found
      this.els.productpanel.element.setAttribute('aria-hidden', true)
      this.els.emptypanel.element.setAttribute('aria-hidden', false)
    } else {
      // Add products to grid
      const productCardHTML = await this.getAllProductCardHTML(records)
      this.els['item-container'].element.innerHTML = productCardHTML

      this.attachProductClickListeners()

      // Add load more button if there are more pages
      if (resultSearch.hasNextPage()) {
        const loadMoreButtonHTML = await this.addLoadMoreButton()
        this.els['product-grid'].element.insertAdjacentHTML(
          'afterend',
          loadMoreButtonHTML,
        )

        this.addLoadMoreEvent()
      }

      // Update collection scroll
      this.updateCollectionScroll()

      // Add filters
      this.els.facets.element.innerHTML = await this.generateFilterMarkup(
        this.manager.options,
      )

      // Add active filters
      this.els['active-facets'].element.innerHTML =
        await this.generateSelectedFilterMarkup(this.manager.selectedFilters())
      this.els['active-facets'].element.dataset.empty =
        this.manager.selectedFilters().length > 0 ? 'false' : 'true'

      // Show product panel
      this.els.productpanel.element.setAttribute('aria-hidden', false)
      this.els.emptypanel.element.setAttribute('aria-hidden', true)

      window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT_GRID.RENDERED)
      window.BAO.dispatchEvent(window.BAO.EVENTS.WISHLIST.CHECK_WISHLIST)
    }

    this.unsetBusyState(600)
  }

  async loadMoreResults (event) {
    event.preventDefault()

    this.setAttribute('aria-busy', true)
    const totalShowingEl = this.querySelector('[data-current-count]')

    // Fetch next page of results
    const nextRes = await this.resultSearch.getPage({
      pageIndex: this.page,
    })

    const nextRecords = nextRes.apiResponse.queryResults[0].records
    this.appendNextPage(nextRecords)

    this.totalShowing = this.totalShowing + nextRecords.length
    totalShowingEl.innerHTML = this.totalShowing

    this.updateResultsLabel()

    const totalResultsFound =
      nextRes.apiResponse.queryResults[0].meta.totalResultsFound

    this.attachProductClickListeners()

    // Hide load more button if no more results
    if (this.totalShowing >= totalResultsFound) {
      const loadMoreButton = document.querySelector(
        '[data-collection-filters-el="pagination load-more"]',
      )
      if (loadMoreButton) {
        loadMoreButton.style.display = 'none'
      }
    } else {
      const loadMoreButtonHTML = await this.addLoadMoreButton()
      const loadMoreButtonContainer = document.querySelector(
        '[data-collection-filters-el="pagination load-more"]',
      )
      if (loadMoreButtonContainer) {
        loadMoreButtonContainer.outerHTML = loadMoreButtonHTML

        this.addLoadMoreEvent()
      }
    }

    this.page = this.page + 1

    // Update collection scroll
    this.updateCollectionScroll()

    this.unsetBusyState(600)

    window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT_GRID.RENDERED)
    window.BAO.dispatchEvent(window.BAO.EVENTS.WISHLIST.CHECK_WISHLIST)
  }

  async appendNextPage (records) {
    const productCardHTML = await this.getAllProductCardHTML(records)
    this.els['item-container'].element.insertAdjacentHTML(
      'beforeend',
      productCardHTML,
    )
  }

  async addLoadMoreButton () {
    const showingCountString =
      window.theme.klevu.strings.searchPagination.showingCountProducts
        .replace(
          /{{ count }}/g,
          `<span data-current-count>${this.totalShowing}</span>`,
        )
        .replace(/{{ total }}/g, this.recordsTotal)

    const loadMoreCardHTML = await this.getAllloadMoreCardHTML()

    return `
      <div
        class="clc-ProductGrid_Pagination clc-ProductGrid_Pagination-loadMore"
        data-collection-filters-el="pagination load-more"
      >
        <div class="pgn-LoadMore" data-collection-scroll-el="pagination">
          <div class="pgn-LoadMore_Cards">
            ${loadMoreCardHTML}
          </div>

          <div class="pgn-LoadMore_Content">
            <p class="pgn-LoadMore_Text fz-13_150">
              ${showingCountString}
            </p>
            <button
              class="pgn-LoadMore_Button btn-Button btn-Button-primary btn-Button-invert"
              data-collection-filters-el="load-more-trigger"
            >
              <span class="btn-Button_Text">
                ${window.theme.klevu.strings.searchPagination.loadMoreProducts}
              </span>
              <span class="btn-Button_Arrow">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 8 8"><path fill="currentColor" fill-rule="evenodd" d="M0 0h8v8H7V1.707L1.354 7.354 1 7.707.293 7l.353-.354L6.293 1H0V0Z" clip-rule="evenodd"/></svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    `
  }

  attachProductClickListeners () {
    const productEls = this.els['product-grid'].element.querySelectorAll(
      '[data-klevu-search-product]',
    )

    this.onProductClick = this.onProductClick.bind(this)

    productEls.forEach((productEl) => {
      productEl.addEventListener('click', this.onProductClick)
    })
  }

  addLoadMoreEvent () {
    const loadMoreButton = document.querySelector(
      '[data-collection-filters-el="load-more-trigger"]',
    )

    if (!loadMoreButton) return

    loadMoreButton.addEventListener('click', this.loadMoreResults.bind(this))
  }

  updateCollectionScroll () {
    const scrollCardWidthTablet = 65
    const scrollCardWidthDesktop = 60
    const scrollCardWidthLargeDesktop = 40

    let totalWidthTablet = scrollCardWidthTablet * this.totalShowing
    let totalWidthDesktop = scrollCardWidthDesktop * this.totalShowing
    let totalWidthLargeDesktop =
      scrollCardWidthLargeDesktop * this.totalShowing

    if (this.resultSearch.hasNextPage()) {
      totalWidthTablet += 65
      totalWidthDesktop += 60
      totalWidthLargeDesktop += 40
    }

    const collectionScroll = document.querySelector('collection-scroll')

    if (collectionScroll) {
      collectionScroll.style.setProperty(
        '--Card_Width_Tablet',
        `${scrollCardWidthTablet}vw`,
      )
      collectionScroll.style.setProperty(
        '--Card_Width_Desktop',
        `${scrollCardWidthDesktop}vw`,
      )
      collectionScroll.style.setProperty(
        '--Card_Width_Large_Desktop',
        `${scrollCardWidthLargeDesktop}vw`,
      )

      collectionScroll.style.setProperty(
        '--Card_Total_Tablet',
        `${totalWidthTablet}vw`,
      )
      collectionScroll.style.setProperty(
        '--Card_Total_Desktop',
        `${totalWidthDesktop}vw`,
      )
      collectionScroll.style.setProperty(
        '--Card_Total_Large_Desktop',
        `${totalWidthLargeDesktop}vw`,
      )

      collectionScroll.dataset.cardSizeTablet = scrollCardWidthTablet
      collectionScroll.dataset.cardSizeDesktop = scrollCardWidthDesktop
      collectionScroll.dataset.cardSizeLargeDesktop =
        scrollCardWidthLargeDesktop

      collectionScroll.dataset.totalCardSizeTablet = totalWidthTablet
      collectionScroll.dataset.totalCardSizeDesktop = totalWidthDesktop
      collectionScroll.dataset.totalCardSizeLargeDesktop =
        totalWidthLargeDesktop

      collectionScroll.dataset.promoSize = 35

      collectionScroll.connectedCallback()
    }
  }

  updateResultsLabel () {
    if (this.recordsTotal < 1) {
      const noResultsTemplate = this.processTemplateString(
        window.theme.klevu.strings.searchNoResults,
      )
      const cleanedTemplate = noResultsTemplate.replace(
        /{{ terms }}/g,
        this.searchTerm,
      )
      this.els.searchtext.element.innerHTML = cleanedTemplate

      return
    }

    const resultsTemplate = JSON.parse(
      this.processTemplateString(
        window.theme.klevu.strings.searchResultsWithCount,
      ),
    )

    const templateKey = this.recordsTotal === 1 ? 'one' : 'other'
    const selectedTemplate = resultsTemplate[templateKey]
    const finalHTML = selectedTemplate
      .replace(/{{ count }}/g, this.recordsTotal)
      .replace(/{{ terms }}/g, this.searchTerm)

    this.els.searchtext.element.innerHTML = finalHTML
  }

  async generateFilterMarkup (filters) {
    // Fetch all metaobject labels first
    const optionsMarkupPromises = filters.map(async (filterEl) => {
      const optionsMarkup = await Promise.all(
        filterEl.options.map(async (option, index) => {
          const isMetaobject = option.value.startsWith(
            'gid //shopify/Metaobject',
          )
          const displayValue = await this.getMetaobjectContent(
            filterEl,
            option,
          )

          if (isMetaobject) {
            return displayValue
          } else {
            return `
            <li class="clc-ProductGridFacet_Item">
              <label class="clc-ProductGridFacet_Label fz-13_150" for="${filterEl.key}-${index}">
                <input
                  class="clc-ProductGridFacet_Checkbox"
                  type="checkbox"
                  name="${filterEl.key}"
                  value="${option.value}"
                  id="${filterEl.key}-${index}"
                  ${option.selected ? 'checked' : ''}
                  data-klevu-search-el="option"
                >
                <span class="clc-ProductGridFacet_Text">
                  ${option.value}
                </span>
              </label>
            </li>
          `
          }
        }),
      )

      let filterListClasses = 'clc-ProductGridFacet_List'
      if (optionsMarkup.length > 5) {
        filterListClasses += ' clc-ProductGridFacet_List-double'
      }

      let filterListHTML = `
        <ul class="${filterListClasses}">
          ${optionsMarkup.join('')}
        </ul>
      `

      // Check if any `li.clc-ProductGridFacet_Item-image` exists
      const containsImageItem = optionsMarkup.some((item) =>
        item.includes('clc-ProductGridFacet_Item-image'),
      )

      let filterItemAttributes = `aria-expanded="${filterEl.options.some((option) => option.selected) ? 'true' : 'false'}"`
      let filterButtonContent = `
        ${filterEl.label}
        <div class="clc-ProductGridFacets_Icon">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
            <path stroke="currentColor" d="m1 1 4 4 4-4"/>
          </svg>
        </div>
      `

      if (containsImageItem) {
        // Modify the markup for carousel case
        filterListHTML = `
          <filter-carousel class="clc-ProductGridFacet_Carousel">
            <ul
              class="clc-ProductGridFacet_List clc-ProductGridFacet_List-image keen-slider"
              data-filter-carousel-el="slides"
              data-initialised="true"
            >
              ${optionsMarkup.join('')}
            </ul>
          </filter-carousel>
        `

        // Update attributes and remove the icon for carousel
        filterItemAttributes = `aria-expanded="true"`
        filterButtonContent = `${filterEl.label}`
      }

      return `
        <div class="clc-ProductGridFacets_Item" data-collection-filters-el="facet" data-filter-accordions-el="item" ${filterItemAttributes} role="treeitem">
          <button type="button" class="clc-ProductGridFacets_FilterLabel fz-12_120" data-filter-accordions-el="trigger">
            ${filterButtonContent}
          </button>

          <div class="clc-ProductGridFacets_ContentContainer">
            <div class="clc-ProductGridFacets_Content" data-filter-accordions-el="content">
              ${filterListHTML}
            </div>
          </div>
        </div>
      `
    })

    return `
      <div class="clc-ProductGridFacets_Items">
        <div class="clc-ProductGridFacets_Item" aria-expanded="true">
          <div class="clc-ProductGridFacets_FilterLabel fz-12_120">
            ${window.theme.klevu.strings.sortByLabel}
          </div>

          <div class="clc-ProductGridFacets_ContentContainer">
            <div class="clc-ProductGridFacets_Content">
              <ul class="clc-ProductGridFacet_List clc-ProductGridFacet_List-double clc-ProductGridFacet_List-sorting">
                <li class="clc-ProductGridFacet_Item">
                  <label class="clc-ProductGridFacet_Label fz-13_150" for="RELEVANCE">
                    <input
                      class="clc-ProductGridFacet_Checkbox clc-ProductGridFacet_Checkbox-radio"
                      type="radio"
                      name="sort_by"
                      value="RELEVANCE"
                      id="RELEVANCE"
                      checked
                    >
                    ${window.theme.klevu.strings.sortByValues.relevance}
                  </label>
                </li>
              </ul>
            </div>
          </div>
        </div>

        ${(await Promise.all(optionsMarkupPromises)).join('')}
      </div>
    `
  }

  async generateSelectedFilterMarkup (selectedFilters) {
    const hasActiveFilters = selectedFilters.length > 0
    this.els['aside-active-indicator'].element.setAttribute(
      'aria-hidden',
      !hasActiveFilters,
    )

    const filterMarkupPromises = selectedFilters.map(
      async (selectedFilterEl) => {
        const label = await this.getMetaobjectLabel(selectedFilterEl.value)

        return `
        <div
          class="clc-ProductGridFacets_ActiveFacet"
          data-collection-filters-el="active-facet"
        >
          <button type="button" class="clc-ProductGridFacets_ActiveLink fz-10_100" name="${selectedFilterEl.key}" value="${selectedFilterEl.value}" data-collection-filters-el="facet-remove">
            ${label}
            <span class="clc-ProductGridFacets_ActiveIcon">
              <svg xmlns="http://ww.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="m6.5 13.5 7-7M13.5 13.5l-7-7"/>
              </svg>
            </span>
          </button>
        </div>
      `
      },
    )

    // Wait for all labels to resolve before rendering
    const filterMarkup = await Promise.all(filterMarkupPromises)

    return `
      <div class="clc-ProductGridFacets_ActiveFacets">
        ${filterMarkup.join('')}
      </div>

      <div class="clc-ProductGridFacets_Remove">
        <button
          data-collection-filters-el="removeAll"
          class="clc-ProductGridFacets_ClearAll btn-Button btn-Button-secondary"
        >
          ${window.theme.klevu.strings.clearAll}
        </button>
      </div>
    `
  }

  unsetBusyState (delay) {
    setTimeout(() => {
      this.setAttribute('aria-busy', false)
    }, delay)
  }

  processTemplateString (str) {
    return str
      .replace(/&quot;/g, '"')
      .replace(/=&gt;/g, ':')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/^"|"$/g, '')
  }

  onProductClick (event) {
    const product = event.target.closest('[data-klevu-search-product]')

    const productClickData = {
      product: {
        id: `${product.dataset.productId}`,
        name: `${product.dataset.productName}`,
        url: `${product.dataset.productUrl}`,
      },
      searchTerm: `${this.searchTerm}`,
    }

    KlevuEvents.searchProductClick(productClickData)
  }

  getQueryParam (param) {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get(param)
  }

  removeExistingLoadMoreButton () {
    const existingLoadMore = document.querySelector(
      '[data-collection-filters-el="pagination load-more"]',
    )
    if (existingLoadMore) {
      existingLoadMore.remove()
    }
  }

  async getAllProductCardHTML (records) {
    // Generate HTML for all product cards
    const productCardHTML = await Promise.all(
      records.map(async (product, index) => {
        const productHTML = await this.getProductCardHTML(product, index)
        return productHTML || ''
      }),
    )
    return productCardHTML.filter(Boolean).join('')
  }

  async getProductCardHTML (product, index) {
    const productUrl = product.url.replace(
      '/products/',
      `${window.Shopify.routes.root}products/`,
    )

    return window.BAO.sectionRenderer
      .fetchSingle('search-product-card', `${productUrl}`)
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
        const myself = newDOM.querySelector('article')

        if (myself) {
          const modSeven = index % 8
          const modEight = (index + 1) % 8

          const addOverlayClass = modSeven === 6 || modEight === 0

          return `
            <li
              class="prd-List_Item${addOverlayClass ? ' prd-List_Item-overlayLeft' : ''}"
              data-collection-filters-el="item"
              data-collection-scroll-el="product"
            >
              ${myself.outerHTML}
            </li>
          `
        }
      })
      .catch(() => {
        return null
      })
  }

  async getAllloadMoreCardHTML () {
    let loadMoreRecords = []

    // Fetch next page of results if available
    if (this.resultSearch.hasNextPage()) {
      const nextRes = await this.resultSearch.getPage({
        pageIndex: this.page,
      })
      loadMoreRecords = nextRes.apiResponse.queryResults[0].records.slice(0, 8)
    }

    // Generate HTML for load more cards
    const loadMoreCardHTML = await Promise.all(
      loadMoreRecords.map(async (product, index) => {
        const productHTML = await this.getloadMoreCardHTML(product, index)
        return productHTML || ''
      }),
    )

    return loadMoreCardHTML.filter(Boolean).join('')
  }

  async getloadMoreCardHTML (product) {
    const productUrl = product.url.replace(
      '/products/',
      `${window.Shopify.routes.root}products/`,
    )

    return window.BAO.sectionRenderer
      .fetchSingle('template-klevu-loadmore-card', `${productUrl}`)
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
        const myself = newDOM.querySelector('.pgn-LoadMore_Card')

        if (myself) return myself.outerHTML
      })
      .catch(() => {
        return null
      })
  }

  async getMetaobjectContent (filterEl, option) {
    const gid = option.value
    if (!gid.startsWith('gid //shopify/Metaobject')) {
      return gid // Not a metaobject, return as-is
    }

    const formattedGid = gid.replace('gid //', 'gid://').trim()

    // GraphQL Query
    const query = `
      {
        metaobject(id: "${formattedGid}") {
          id
          handle
          fields {
            key
            value
          }
        }
      }
    `

    try {
      const response = await fetch('/api/2025-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.theme.shopifyAccessToken,
        },
        body: JSON.stringify({ query }),
      })

      const result = await response.json()
      let itemHTML = ''

      if (result.data?.metaobject?.fields[0]?.key === 'title') {
        itemHTML = `
          <li class="clc-ProductGridFacet_Item">
            <label class="clc-ProductGridFacet_Label fz-13_150" for="${filterEl.key}-${result.data?.metaobject?.handle}">
              <input
                class="clc-ProductGridFacet_Checkbox"
                type="checkbox"
                name="${filterEl.key}"
                value="${option.value}"
                id="${filterEl.key}-${result.data?.metaobject?.handle}"
                ${option.selected ? 'checked' : ''}
                data-klevu-search-el="option"
              >

              <span class="clc-ProductGridFacet_Text">
                ${result.data?.metaobject?.fields[0]?.value}
              </span>
            </label>
          </li>
        `
      }

      if (result.data?.metaobject?.fields[0]?.key === 'image') {
        const mediaGid = result.data?.metaobject?.fields[0]?.value
        const imageData = await this.getModelImage(mediaGid)

        const imageUrl = imageData?.url || ''
        const altText = imageData?.alt || ''

        itemHTML = `
          <li
            class="clc-ProductGridFacet_Item clc-ProductGridFacet_Item-image keen-slider__slide"
            data-filter-carousel-el="slide"
          >
            <label class="clc-ProductGridFacet_Label fz-13_150" for="${filterEl.key}-${result.data?.metaobject?.handle}">
              <input
                class="clc-ProductGridFacet_Checkbox"
                type="checkbox"
                name="${filterEl.key}"
                value="${option.value}"
                id="${filterEl.key}-${result.data?.metaobject?.handle}"
                ${option.selected ? 'checked' : ''}
                data-klevu-search-el="option"
              >
              <div class="clc-ProductGridFacet_Image">
                <div class="rsp-Image" style="--Image_AspectRatio: 100%; --Aspect_Ratio: 500 / 500;">
                  <img
                    src="${imageUrl}"
                    alt="${altText}"
                    width="500"
                    height="500"
                    loading="eager"
                    fetchpriority="low"
                    class="rsp-Image_Image"
                    sizes="90px"
                  >
                </div>
              </div>

              <span class="clc-ProductGridFacet_Text">
                ${result.data?.metaobject?.fields[2]?.value}
              </span>
            </label>
          </li>
        `
      }

      if (result.data?.metaobject?.fields[0]?.key === 'hex') {
        itemHTML = `
          <li class="clc-ProductGridFacet_Item">
            <label class="clc-ProductGridFacet_Label fz-13_150" for="${filterEl.key}-${result.data?.metaobject?.handle}">
              <input
                class="clc-ProductGridFacet_Checkbox clc-ProductGridFacet_Checkbox-swatch"
                type="checkbox"
                name="${filterEl.key}"
                value="${option.value}"
                id="${filterEl.key}-${result.data?.metaobject?.handle}"
                ${option.selected ? 'checked' : ''}
                data-klevu-search-el="option"
              >

              <span class="clc-ProductGridFacet_Swatch" style="--Swatch: ${result.data?.metaobject?.fields[0]?.value};"></span>

              <span class="clc-ProductGridFacet_Text">
                ${result.data?.metaobject?.fields[1]?.value}
              </span>
            </label>
          </li>
        `
      }

      return itemHTML
    } catch (error) {
      console.error('Failed to fetch metaobject itemHTML:', error)
      return gid // Fallback to original value
    }
  }

  async getMetaobjectLabel (gid) {
    if (!gid.startsWith('gid //shopify/Metaobject')) {
      return gid // Not a metaobject, return as-is
    }

    const formattedGid = gid.replace('gid //', 'gid://').trim()

    // GraphQL Query
    const query = `
      {
        metaobject(id: "${formattedGid}") {
          fields {
            key
            value
          }
        }
      }
    `

    try {
      const response = await fetch('/api/2025-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.theme.shopifyAccessToken,
        },
        body: JSON.stringify({ query }),
      })

      const result = await response.json()
      if (!result.data?.metaobject?.fields) return gid // Fallback to original value if no fields found

      // Find the correct label field based on the type of metaobject
      const fields = result.data.metaobject.fields
      const titleField = fields.find((field) => field.key === 'title')?.value
      // const colorField = fields.find(field => field.key === 'hex')?.value
      const colorLabelField = fields.find(
        (field) => field.key === 'name',
      )?.value
      const imageNameField = fields.find(
        (field) => field.key === 'image',
      )?.value

      return titleField || colorLabelField || imageNameField || gid
    } catch (error) {
      console.error('Failed to fetch metaobject label:', error)
      return gid
    }
  }

  async getModelImage (gid) {
    if (!gid.startsWith('gid://shopify/MediaImage')) {
      return null // Not a valid MediaImage GID
    }

    // GraphQL Query
    const query = `
      {
        node(id: "${gid}") {
          ... on MediaImage {
            image {
              url
              altText
            }
          }
        }
      }
    `

    try {
      const response = await fetch('/api/2025-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.theme.shopifyAccessToken,
        },
        body: JSON.stringify({ query }),
      })

      const result = await response.json()
      const imageUrl = result.data?.node?.image?.url
      const altText = result.data?.node?.image?.altText || ''

      return imageUrl ? { url: imageUrl, alt: altText } : null
    } catch (error) {
      console.error('Failed to fetch media image:', error)
      return null
    }
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', KlevuSearch)
}
